<template>
  <FormHeader :formStep="formStep" @onBackClick="onBack" />
  <FormContainer>
    <TransitionWrapper>
      <FormTitle :class="$style.formTitle">
        {{ t('renovation.title') }}
        <RenovationTooltip :class="$style.tooltip" />
      </FormTitle>
      <FormContent>
        <Space size="8px" direction="vertical">
          <FormRadioBoxItem
            :label="t('answer.yes')"
            name="renovation"
            value="1"
            :checked="Number(formData.isRenovated).toString()"
            @onClick="onItemClick"
          />
          <FormRadioBoxItem
            :label="t('answer.no')"
            name="renovation"
            value="0"
            :checked="Number(formData.isRenovated).toString()"
            @onClick="onItemClick"
          />
        </Space>
      </FormContent>
    </TransitionWrapper>
    <ValuationFunnelErrorText v-if="isShowErrorMessage" />
  </FormContainer>
</template>

<script lang="ts" setup>
import { storeToRefs } from 'pinia';
import { onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';

import {
  FormContainer,
  FormContent,
  FormHeader,
  FormRadioBoxItem,
  FormTitle,
  Space,
  TransitionWrapper,
} from '@/components/common';
import RenovationTooltip from '@/components/common/FormTooltip/RenovationTooltip.vue';
import ValuationFunnelErrorText from '@/components/common/ValuationFunnelErrorText/ValuationFunnelErrorText.vue';
import { useTrackingWithTreatment } from '@/composables/useTrackingWithTreatment';
import { usePropertyValuationStore } from '@/stores/propertyValuation';
import { getStepInputs, isPropertyDetailDataPrefilled } from '@/tracking/tracking';
import { ValuationFlow } from '@/types';

const propertyValuationStore = usePropertyValuationStore();
const {
  data: formData,
  step: formStep,
  propertyDetailsData,
  selectedPropertyDetailIndex,
} = storeToRefs(propertyValuationStore);
const { t, locale } = useI18n();
const route = useRoute();
const { trackVirtualPageViewWithTreatment } = useTrackingWithTreatment();

// Reactive data
const isShowErrorMessage = ref(false);

// Methods
const onNext = (): void => {
  const { isRenovated } = formData.value;

  if (isRenovated === undefined) {
    isShowErrorMessage.value = true;
    return;
  }

  propertyValuationStore.navigateNext(ValuationFlow.Renovation);
};

const onBack = (): void => {
  propertyValuationStore.navigateBack(ValuationFlow.Renovation);
};

const onItemClick = (value: string): void => {
  isShowErrorMessage.value = false;
  propertyValuationStore.setFormData({
    isRenovated: !!parseInt(value),
  });
  onNext();
};

// Lifecycle (replacing mounted)
onMounted(() => {
  trackVirtualPageViewWithTreatment({
    subType: 'input_renovation',
    locale: locale.value,
    stepInputs: getStepInputs(
      {
        ...formData.value,
        aptDetails: isPropertyDetailDataPrefilled(
          propertyDetailsData.value,
          selectedPropertyDetailIndex.value,
        ),
      },
      route.query.source?.toString() || '',
      formStep.value,
    ),
  });
});
</script>

<style lang="scss" module>
@use '@/assets/variables' as *;

.formTitle {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: $space-50;
}
</style>
